import { useMemo, useState } from "react";
import Combobox, { type ComboboxEintrag } from "../Combobox";
import DokumentLightbox, { type Dokument } from "../DokumentLightbox";
import { belegDateiUrl, euro, fmtDate } from "../../lib/belege";
import type { Vertrag } from "../../lib/lieferantVertraege";
import { fehlermeldung } from "../../lib/lieferantVertraege";
import {
  useBelegKandidaten,
  useUnterlageLoesen,
  useUnterlageZuordnen,
} from "../../hooks/useLieferantVertraege";

/**
 * Vertragsunterlagen am Vertrag: Jahresrechnung mit Ratenplan, Gutschrift beim
 * Vertragswechsel, 0-€-Rechnung. Sie beschreiben den Vertrag und belegen keine
 * Zahlung — deshalb stehen sie hier und nicht in der Belegverarbeitung, und sie
 * gehen nie an DATEV. Gebucht wird der einzelne Einzug (Perioden-Tabelle).
 *
 * „Als Unterlage ablegen" holt einen Beleg-Entwurf desselben Lieferanten
 * hierher (z. B. die nächste Jahresrechnung aus dem Mail-Import);
 * „zurück in die Belege" macht ihn wieder zum Entwurf.
 */
export default function VertragUnterlagen({ vertrag }: { vertrag: Vertrag }) {
  const [fehler, setFehler] = useState("");
  const [waehlen, setWaehlen] = useState(false);
  const [dok, setDok] = useState<Dokument>(null);
  const kandidaten = useBelegKandidaten(waehlen ? vertrag.id : null);
  const zuordnen = useUnterlageZuordnen();
  const loesen = useUnterlageLoesen();
  const melden = (e: unknown) => setFehler(fehlermeldung(e));

  /* Nur, was noch Entwurf ist: ein erfasster oder verbuchter Beleg ist schon
     Buchhaltung (der Server sperrt das zusätzlich). */
  const eintraege = useMemo<ComboboxEintrag[]>(
    () => (kandidaten.data ?? [])
      .filter((k) => k.status === "entwurf" || k.status === "eingereicht")
      .map((k) => ({
        id: k.id,
        label: `${fmtDate(k.datum)} · ${euro(k.bruttobetrag)}`,
        zusatz: k.rechnungsnummer ? `Nr. ${k.rechnungsnummer}` : undefined,
      })),
    [kandidaten.data],
  );

  function ablegen(belegId: string) {
    if (!belegId) return;
    setFehler("");
    zuordnen.mutate(
      { vertragId: vertrag.id, belegId },
      { onSuccess: () => setWaehlen(false), onError: melden },
    );
  }

  return (
    <section className="vtr-unterlagen" aria-label="Vertragsunterlagen">
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
      <h3 className="vtr-unterkopf">Vertragsunterlagen</h3>
      {fehler && <p className="vtr-fehler" role="alert">{fehler}</p>}
      {vertrag.unterlagen.length ? (
        <ul className="vtr-unterlagenliste">
          {vertrag.unterlagen.map((u) => (
            <li key={u.id}>
              <div className="vtr-unterlage-kopf">
                <span>{fmtDate(u.datum)}</span>
                {u.rechnungsnummer && <span>Nr. {u.rechnungsnummer}</span>}
                <strong>{euro(u.bruttobetrag)}</strong>
              </div>
              {u.notiz && <p className="vtr-zeilennotiz">{u.notiz}</p>}
              <div className="vtr-aktionen">
                {u.hat_datei && (
                  // In der Dokumentansicht statt als rohes PDF — das zeigte am
                  // Telefon nur Seite 1 oder lud die Datei herunter (30.10.2026).
                  <button
                    type="button"
                    className="vtr-klein"
                    onClick={() => setDok({
                      titel: `Unterlage ${u.rechnungsnummer || fmtDate(u.datum)}`,
                      url: belegDateiUrl(u.id),
                      belegId: u.id,
                    })}
                  >
                    Ansehen
                  </button>
                )}
                <button
                  type="button"
                  className="vtr-klein"
                  disabled={loesen.isPending}
                  onClick={() => loesen.mutate({ vertragId: vertrag.id, belegId: u.id }, { onError: melden })}
                >
                  zurück in die Belege
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="vtr-leer">Keine Unterlagen abgelegt.</p>
      )}
      {waehlen ? (
        <div className="vtr-unterlage-wahl">
          <Combobox
            wert=""
            eintraege={eintraege}
            onWaehlen={ablegen}
            platzhalter={kandidaten.isLoading ? "Lädt …" : "Beleg-Entwurf suchen …"}
            ariaLabel="Beleg als Vertragsunterlage ablegen"
            disabled={zuordnen.isPending}
          />
          <button type="button" className="vtr-klein" onClick={() => setWaehlen(false)}>Abbrechen</button>
        </div>
      ) : (
        <button type="button" className="vtr-klein" onClick={() => setWaehlen(true)}>
          Beleg als Unterlage ablegen
        </button>
      )}
    </section>
  );
}
